import React from 'react';
import { SectionHeading } from './ui/SectionHeading';
import { PlayCircle, Star } from 'lucide-react';
type Testimonial = {
  url: string;
  name?: string;
  role?: string;
};
interface SocialProofProps {
  title?: string;
  subtitle?: string;
  testimonials?: Testimonial[];
  /** Index of the testimonial to feature in the center (defaults to the middle one). */
  featuredIndex?: number;
}
const DEFAULT_TESTIMONIALS: Testimonial[] = [{
  url: 'https://www.instagram.com/reel/DXxK24tuvAn/embed'
}, {
  url: 'https://www.instagram.com/reel/DVjhT88DH2c/embed',
  name: 'Imane Guessab',
  role: 'Étudiante en BTS NDRC'
}, {
  url: 'https://www.instagram.com/reel/DZ2OSr5N8tT/embed'
}];
function VideoFrame({
  url
}: {
  url: string;
}) {
  return <div className="relative rounded-[2rem] overflow-hidden bg-slate-100 aspect-[9/16]" data-id="element-487" data-mp="3ur681ay6h3jk">
      <iframe src={url} title="Témoignage d'alternant Zillenium" className="absolute inset-0 w-full h-full" frameBorder="0" scrolling="no" allowTransparency={true} data-id="element-488" data-mp="mbmj2o4ji7ab"></iframe>
    </div>;
}
export function SocialProof({
  title = 'Ils recommandent leur formation',
  subtitle = 'Découvrez les témoignages de nos alternants qui partagent leur expérience chez Zillenium.',
  testimonials = DEFAULT_TESTIMONIALS,
  featuredIndex
}: SocialProofProps) {
  const featured = featuredIndex !== undefined ? featuredIndex : Math.floor(testimonials.length / 2);
  const featuredItem = testimonials[featured];
  const supporting = testimonials.filter((_, i) => i !== featured);
  const isThreeUp = testimonials.length >= 3;
  return <section className="py-24 bg-white relative overflow-hidden" data-id="element-489" data-mp="2mxquu9s8sief">
      <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-lavande rounded-full blur-[100px] opacity-30 pointer-events-none" data-id="element-490" data-mp="2i0gxm7ym32g1" />
      <div className="container max-w-6xl mx-auto relative z-10" data-id="element-491" data-mp="skr8zyx9qryp">
        <div className="text-center mb-16" data-id="element-492" data-mp="nmfydlz5gdff">
          <div className="inline-flex items-center justify-center p-3 bg-brand-orange/10 rounded-2xl mb-6" data-id="element-493" data-mp="nnr2jmwibn72">
            <Star className="w-8 h-8 text-brand-orange" data-id="element-494" data-mp="1hb5b1n1v2xfc" />
          </div>
          <SectionHeading title={title} subtitle={subtitle} centered className="mb-0" data-id="element-495" data-mp="1hbf1tcuqd8m3" />
        </div>

        {isThreeUp ? <>
            {/* Desktop: featured center + two supporting on the sides */}
            <div className="hidden lg:grid grid-cols-3 gap-8 items-center" data-id="element-496" data-mp="1w7mlmc28880u">
              <div className="transform scale-90 opacity-80 hover:scale-95 hover:opacity-100 transition-all duration-500" data-id="element-497" data-mp="209hohkzt2tgq">
                <div className="bg-white p-4 rounded-[2.5rem] shadow-2xl border border-slate-100" data-id="element-498" data-mp="2520xe4njt0f7">
                  <VideoFrame url={supporting[0].url} data-id="element-499" data-mp="29yho96m4tsvz" />
                </div>
              </div>

              <FeaturedCard item={featuredItem} data-id="element-500" data-mp="2f06yik1j62j0" />

              <div className="transform scale-90 opacity-80 hover:scale-95 hover:opacity-100 transition-all duration-500" data-id="element-501" data-mp="2jy05hfanr0kc">
                <div className="bg-white p-4 rounded-[2.5rem] shadow-2xl border border-slate-100" data-id="element-502" data-mp="8awy4znnbs7u">
                  <VideoFrame url={supporting[1].url} data-id="element-503" data-mp="3bakip3wo9yi" />
                </div>
              </div>
            </div>

            {/* Mobile / tablet: featured first, then supporting stacked */}
            <div className="lg:hidden space-y-8 max-w-md mx-auto" data-id="element-504" data-mp="3pja53ro481yn">
              <FeaturedCard item={featuredItem} data-id="element-505" data-mp="3pl4r79b4i04b" />
              {supporting.map((t, i) => <div key={i} className="bg-white p-4 rounded-[2.5rem] shadow-xl border border-slate-100" data-id="element-506" data-mp="r37hja2p4xfk" data-mpi={i}>
                  <VideoFrame url={t.url} data-id="element-507" data-mp="haa0779fxeij" />
                </div>)}
            </div>
          </> /* One or two videos: prominent side-by-side grid */ : <div className="grid sm:grid-cols-2 gap-8 max-w-3xl mx-auto" data-id="element-508" data-mp="19s5vjshitq19">
            {testimonials.map((t, i) => i === featured ? <FeaturedCard key={i} item={t} data-id="element-509" data-mp="14zl2it6fvgys" /> : <div key={i} className="bg-white p-4 rounded-[2.5rem] shadow-xl border border-slate-100 transform hover:-translate-y-1 transition-transform duration-500" data-id="element-510" data-mp="phutgdylum0z">
                  {(t.name || t.role) && <div className="flex items-center justify-between mb-4 px-2" data-id="element-511" data-mp="kjjoohn631pm">
                      <div data-id="element-512" data-mp="189dwgksmq030">
                        {t.name && <h3 className="font-bold text-primary-dark text-lg" data-id="element-513" data-mp="18a5tm01vk5ve">
                            {t.name}
                          </h3>}
                        {t.role && <p className="text-sm text-brand-orange font-medium" data-id="element-514" data-mp="yhhzjd2a4g0d">
                            {t.role}
                          </p>}
                      </div>
                      <PlayCircle className="w-8 h-8 text-primary" data-id="element-515" data-mp="1r65c8k8ypx2b" />
                    </div>}
                  <VideoFrame url={t.url} data-id="element-516" data-mp="1w0t6kzqndlot" />
                </div>)}
          </div>}
      </div>
    </section>;
}
function FeaturedCard({
  item
}: {
  item: Testimonial;
}) {
  return <div className="transform hover:-translate-y-2 transition-all duration-500 z-10" data-id="element-517" data-mp="3qex8wukda3h9">
      <div className="bg-primary p-1 rounded-[2.5rem] shadow-2xl" data-id="element-518" data-mp="3lg3t2vhly0dx">
        <div className="bg-white p-4 rounded-[2.4rem]" data-id="element-519" data-mp="3lgvpg8j14259">
          <div className="flex items-center justify-between mb-4 px-2" data-id="element-520" data-mp="2xpzskwzpugr5">
            <div data-id="element-521" data-mp="2sreounl99exy">
              <h3 className="font-bold text-primary-dark text-lg" data-id="element-522" data-mp="2sry6e36zu1bg">
                {item.name ?? 'Témoignage alternant'}
              </h3>
              {item.role && <p className="text-sm text-brand-orange font-medium" data-id="element-523" data-mp="36ouxm5r0xksv">
                  {item.role}
                </p>}
            </div>
            <PlayCircle className="w-8 h-8 text-primary" data-id="element-524" data-mp="lzxbmvsj9imr" />
          </div>
          <VideoFrame url={item.url} data-id="element-525" data-mp="hzwommrdbz43" />
        </div>
      </div>
    </div>;
}